import React, { useState, useEffect, useCallback } from 'react';
import { useSearchAndSort } from '../../hooks/useSearchAndSort';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  TextField
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { styled } from '@mui/system';
import { format } from 'date-fns';
import DataTable from '../../components/common/DataTable';
import { getBlogs, deleteBlogById, getBlogById } from '../../services/BlogService';
import { contentColumns } from '../../constants/tableColumns';
import PublishBlogModal from '../../components/modals/PublishBlogModal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

const PageWrapper = styled(Box)({
  backgroundColor: '#F5F6F7',
  minHeight: '100vh',
  padding: '24px'
});

const PageHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px'
});

const PageTitle = styled(Typography)({
  color: '#0C53B7',
  fontWeight: 600,
  fontSize: '24px'
});

const TabButton = styled(Button)(({ theme, selected }) => ({
  borderRadius: 50,
  padding: "8px 20px",
  fontWeight: 600,
  textTransform: "none",
  fontSize: "14px",
  transition: "all 0.3s ease",
  minWidth: 160,
  backgroundColor: selected ? theme.palette.primary.main : "transparent",
  color: selected ? "white" : theme.palette.text.primary,
  "&:hover": {
    backgroundColor: selected
      ? theme.palette.primary.dark
      : "rgba(0, 0, 0, 0.04)",
  },
}));

const TabContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "8px",
  padding: "6px",
  backgroundColor: "#f0f1ff",
  borderRadius: 50,
  width: "fit-content",
}));

const FIELD_MAP = new Map([
  ['title', 'title'],
  ['contentType', 'contentType'],
  ['sortOrder', 'sortOrder'],
  ['isActive', 'isActive'],
  ['published', 'published'],
  ['publishedDate', 'createDatetime'],
]);

const getSortByField = (columnId) => FIELD_MAP.get(columnId) || columnId;

const PublishBlogMaster = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [blogs, setBlogs] = useState([]);
  const [totalCount, setTotalCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const[action, setAction] = useState('create');
  const[blogId,setBlogId] =  useState(null)
  const activeTab = parseInt(searchParams.get('tab') || '0');
  const sectionKey = `content-${activeTab}`;
  const { searchQuery, setSearchQuery, handleSort, searchAndSortParams, sortBy, order } = useSearchAndSort(500, sectionKey);
  const userPermission = useSelector((state) => state.user.userPermissions);
  const [permission, setPermissions] = useState({});
  useEffect(() => {
    console.log("User Permissions:", userPermission);
    const moduleList = userPermission?.map((e) => e?.module);
    if (moduleList && moduleList.length > 0) {
      const hasLeadsPermission = moduleList.includes("blogs");
      if (!hasLeadsPermission) {
        //   showToast("You do not have permission to access Leads", "error");
        navigate("/dashboard");
      } else {
        console.log(
          "User Permissions for Leads:",
          userPermission.find(
            (e) => e.module === "blogs"
          )
        );
        setPermissions(
          JSON.parse(
            userPermission.find((e) => e.module === "blogs")?.permission
          ) || null
        );
      }
    }
  }, [userPermission]);

  useEffect(() => {
    fetchBlogs();
  }, [page, rowsPerPage, searchAndSortParams.searchQuery, searchAndSortParams.sortBy, searchAndSortParams.order, activeTab]);

  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      const sortByField = searchAndSortParams.sortBy ? getSortByField(searchAndSortParams.sortBy) : searchAndSortParams.sortBy;
      const response = await getBlogs({ 
        page, 
        size: rowsPerPage, 
        contentType: activeTab === 0 ? 'blog' : 'knowledge',
        ...searchAndSortParams,
        ...(sortByField && { sortBy: sortByField })
      });
      if (response?.responseCode === 200 || response?.statusCode === 200) {
        const list = Array.isArray(response?.result?.result)
          ? response.result.result
          : (Array.isArray(response?.result) ? response.result : []);
        setBlogs(list);
        const count = Number.isFinite(response?.rowCount)
          ? response.rowCount
          : (Number.isFinite(response?.result?.rowCount) ? response.result.rowCount : null);
        setTotalCount(count);
      } else {
        setBlogs([]);
        setTotalCount(null);
      }
    } catch (error) {
      console.error(error);
      setSnackbar({ open: true, message: 'Failed to fetch blogs.', severity: 'error' });
      setTotalCount(null);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchAndSortParams.searchQuery, searchAndSortParams.sortBy, searchAndSortParams.order, activeTab]);

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const handleOpenModal = (blog = null) => {
    if (blog) {
      setAction('update');
    } else {
      setAction('create');
    }
    setSelectedBlog(blog);
    setModalOpen(true);
  };

  const handleEditBlog = (blogId) => {
    getBlogById(blogId).then((response) => {
      if (response?.responseCode === 200) {
        setSelectedBlog(response.result);
        setBlogId(blogId)
        // setSelectedBlog(blog);
        setAction('update');
        setModalOpen(true);
      } else {
        setSnackbar({ open: true, message: 'Failed to fetch blog details.', severity: 'error' });
      }
    }).catch((error) => {
      console.error(error);
      setSnackbar({ open: true, message: 'Failed to fetch blog details.', severity: 'error' });
    })
  };

  const handleCloseModal = () => {
    setSelectedBlog(null);
    setModalOpen(false);
    fetchBlogs(); // refresh after update/create
  };

  const handleDelete = async (blogId) => {
    try {
      await deleteBlogById(blogId);
      setSnackbar({ open: true, message: 'Blog deleted successfully.', severity: 'success' });
      fetchBlogs();
    } catch (error) {
      console.error(error);
      setSnackbar({ open: true, message: 'Failed to delete blog.', severity: 'error' });
    }
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (newValue) => {
    setSearchParams({ tab: newValue.toString() });
    setPage(0);
  };

  const getFilteredBlogs = () => {
    const baseList = totalCount != null
      ? blogs
      : (activeTab === 0
        ? blogs.filter((blog) => (blog.contentType || '').toLowerCase() === 'blog' || !blog.contentType)
        : blogs.filter((blog) => (blog.contentType || '').toLowerCase() === 'knowledge'));

    const searchTermToUse = searchAndSortParams.searchQuery || '';
    if (!searchTermToUse.trim()) return baseList;

    const searchTerm = searchTermToUse.toLowerCase();
    return baseList.filter((blog) => {
      const searchableFields = [
        blog.title,
        blog.contentType,
        blog.isActive?.toString(),
        blog.published?.toString(),
      ];

      return searchableFields.some((field) => 
        field?.toLowerCase().includes(searchTerm)
      );
    });
  };

  const filteredBlogs = getFilteredBlogs();

  const handleSearchChange = (event) => setSearchQuery(event.target.value);

  return (
    <PageWrapper>
      <PageHeader>
        <PageTitle>Publish Content</PageTitle>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by Title, Content Type, Active Status, or Published Status..."
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ minWidth: 400 }}
            size="small"
          />
          {permission?.create === 1 && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleOpenModal()}
              sx={{ textTransform: 'none' }}
            >
              Create Content
            </Button>
          )}
        </Box>
      </PageHeader>

      <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        <TabContainer>
          <TabButton
            selected={activeTab === 0}
            onClick={() => handleTabChange(0)}
          >
            BLOGS
          </TabButton>
          <TabButton
            selected={activeTab === 1}
            onClick={() => handleTabChange(1)}
          >
            KNOWLEDGE
          </TabButton>
        </TabContainer>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (filteredBlogs.length === 0 && !(totalCount > 0)) ? (
        <Typography>
          {activeTab === 0 ? 'No blogs found.' : 'No knowledge articles found.'}
        </Typography>
      ) : (
        <DataTable
          permissions={permission}
          columns={contentColumns.columns}
          data={filteredBlogs}
          loading={loading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount ?? undefined}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          onEditClick={(row) => handleEditBlog(row.id)}
          onDeleteClick={(row) => handleDelete(row.id)}
          onSort={handleSort}
          currentSortBy={sortBy}
          currentOrder={order}
          noDataMessage={activeTab === 0 ? 'No blogs found.' : 'No knowledge articles found.'}
          customCellRenderer={{
            contentType: (row) => {
              if (!row.contentType) return "N/A";
              return row.contentType.charAt(0).toUpperCase() + row.contentType.slice(1);
            },
            isActive: (row) => row.isActive ? 'Active' : 'Inactive',
            published: (row) => row.published ? 'Yes' : 'No',
            publishedDate: (row) => format(new Date(row?.publishedDate), 'dd MMM yyyy'),
          }}
        />
      )}

      {/* Blog Modal */}
      <PublishBlogModal open={modalOpen} blog={selectedBlog} onClose={handleCloseModal} action={action} blogId={blogId}/>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={handleCloseSnackbar} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageWrapper>
  );
};

export default PublishBlogMaster;
